import { LitElement, html, css } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { unsafeHTML } from 'lit/directives/unsafe-html.js';
import { theme, forms } from '../styles/index.js';
import { showToast } from './toast-manager.js';
import { confirmElicit } from '../utils/elicit.js';
import { templates } from './studio-templates.js';
import { sparkle, refresh as refreshIcon, check, xMark, file as fileIcon } from '../icons.js';
import { trapFocus } from '../utils/focus-trap.js';

declare const marked: { parse: (md: string) => string };

interface MarketplaceItem {
  name: string;
  description: string;
  author: string;
  tags: string[];
  marketplace: string;
  version: string;
  icon?: string;
  internal?: boolean;
  installed?: boolean;
  hasUpdate?: boolean;
  latestVersion?: string;
  displayName?: string;
}

interface MarketplaceSource {
  name: string;
  source: string;
  sourceType: string;
  enabled: boolean;
  builtIn: boolean;
  photonCount: number;
}

@customElement('marketplace-view')
export class MarketplaceView extends LitElement {
  static styles = [
    theme,
    forms,
    css`
      :host {
        display: block;
        height: 100%;
        display: flex;
        flex-direction: column;
      }

      .toolbar {
        display: flex;
        gap: var(--space-md);
        margin-bottom: var(--space-lg);
      }

      .search-box {
        flex: 1;
        position: relative;
      }

      .search-box input {
        padding: var(--space-sm) var(--space-lg);
        border-radius: var(--radius-md);
        font-size: var(--text-lg);
        transition: all 0.2s;
      }

      .grid {
        display: grid;
        grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
        gap: var(--space-md);
        overflow-y: auto;
        padding-bottom: var(--space-xl);
      }

      .card {
        background: var(--bg-glass);
        border: 1px solid var(--border-glass);
        border-radius: var(--radius-md);
        padding: var(--space-md);
        display: flex;
        flex-direction: column;
        cursor: pointer;
        transition:
          transform 0.2s,
          box-shadow 0.2s;
      }

      .card:hover {
        transform: translateY(2px);
        box-shadow: 0 8px 24px rgba(0, 0, 0, 0.2);
        border-color: var(--accent-secondary);
      }

      .card-body {
        display: flex;
        gap: var(--space-md);
        min-height: 88px;
      }

      .card-icon-large {
        width: 88px;
        height: 88px;
        border-radius: var(--radius-md);
        background: var(--bg-glass-strong);
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 3.5rem;
        flex-shrink: 0;
        font-weight: 600;
        color: var(--t-primary);
      }

      .card-icon-large.initials-icon {
        background: hsl(
          var(--icon-hue, 0),
          var(--initials-bg-sat, 35%),
          var(--initials-bg-lum, 22%)
        );
        color: hsl(var(--icon-hue, 0), var(--initials-fg-sat, 60%), var(--initials-fg-lum, 75%));
      }

      .card-icon-large.emoji-icon {
        background: var(--bg-glass-strong);
      }

      .card-content {
        flex: 1;
        min-width: 0;
        display: flex;
        flex-direction: column;
        gap: 2px;
      }

      .card-title-row {
        display: flex;
        align-items: center;
        flex-wrap: wrap;
        gap: 4px var(--space-sm);
      }

      .card-title {
        font-weight: 600;
        font-size: var(--text-lg);
        color: var(--t-primary);
      }

      .card-author {
        font-size: var(--text-sm);
        color: var(--t-muted);
      }

      .card-desc {
        font-size: var(--text-md);
        color: var(--t-muted);
        line-height: 1.4;
        display: -webkit-box;
        -webkit-line-clamp: 2;
        -webkit-box-orient: vertical;
        overflow: hidden;
      }

      .card-footer {
        display: flex;
        align-items: center;
        gap: var(--space-sm);
        padding-top: var(--space-sm);
        border-top: 1px solid var(--border-glass);
        margin-top: var(--space-sm);
      }

      .card-footer .btn-secondary-push {
        margin-left: auto;
      }

      .tags {
        display: flex;
        gap: 4px;
        flex-wrap: wrap;
        margin-top: var(--space-xs);
      }

      .tag {
        font-size: var(--text-xs);
        padding: 2px 8px;
        background: rgba(255, 255, 255, 0.05);
        border-radius: var(--radius-full);
        color: var(--t-muted);
      }

      .source-pill {
        font-size: var(--text-2xs);
        padding: 2px 8px;
        border-radius: var(--radius-full);
        font-weight: 600;
        text-transform: uppercase;
        letter-spacing: 0.05em;
      }

      .source-official {
        background: linear-gradient(135deg, hsl(210, 100%, 50%), hsl(240, 100%, 60%));
        color: white;
      }

      .source-community {
        background: linear-gradient(135deg, hsl(150, 60%, 45%), hsl(180, 60%, 45%));
        color: white;
      }

      .source-local {
        background: rgba(255, 255, 255, 0.1);
        color: var(--t-muted);
        border: 1px solid var(--border-glass);
      }

      .source-internal {
        background: linear-gradient(135deg, hsl(280, 60%, 50%), hsl(320, 60%, 50%));
        color: white;
      }

      .card.internal {
        border-color: hsl(280, 40%, 40%);
      }

      .card-meta {
        display: flex;
        align-items: center;
        gap: var(--space-xs);
        flex-shrink: 0;
      }

      /* Marketplace filter pills */
      .source-filters {
        display: flex;
        gap: 8px;
        flex-wrap: wrap;
        margin-bottom: var(--space-lg);
        padding: 2px; /* Add padding to prevent focus ring clipping */
        overflow: visible; /* Ensure outline indicator displays */
      }

      .filter-pill {
        padding: 6px 12px;
        border-radius: var(--radius-lg);
        background: var(--bg-glass);
        border: 1px solid var(--border-glass);
        font-size: var(--text-md);
        color: var(--t-secondary);
        cursor: pointer;
        transition:
          color 0.2s,
          background 0.2s,
          border-color 0.2s;
        display: flex;
        align-items: center;
        gap: 6px;
        position: relative;
        z-index: 1;
      }

      .filter-pill:hover {
        border-color: var(--accent-primary);
        color: var(--t-primary);
        background: rgba(59, 130, 246, 0.1);
      }

      .filter-pill:focus-visible {
        outline: 2px solid rgba(59, 130, 246, 0.6);
        outline-offset: 2px;
        border-color: var(--accent-primary);
      }

      .filter-pill.active {
        background: var(--accent-primary);
        border-color: var(--accent-primary);
        color: white;
      }

      .filter-pill .count {
        background: rgba(255, 255, 255, 0.2);
        padding: 2px 6px;
        border-radius: var(--radius-full);
        font-size: var(--text-xs);
        font-weight: 500;
      }

      .filter-pill.active .count {
        background: rgba(255, 255, 255, 0.3);
      }

      .filter-pill .remove-btn {
        opacity: 0;
        margin-left: 4px;
        padding: 0;
        background: none;
        border: none;
        color: inherit;
        cursor: pointer;
        font-size: 14px;
        line-height: 1;
      }

      .filter-pill:hover .remove-btn {
        opacity: 0.6;
      }

      .filter-pill .remove-btn:hover {
        opacity: 1;
      }

      .actions {
        display: flex;
        justify-content: flex-end;
      }

      .btn-install {
        background: var(--accent-primary);
        color: white;
        border: none;
        padding: 6px 12px;
        border-radius: var(--radius-sm);
        cursor: pointer;
        font-weight: 500;
        transition: opacity 0.2s;
      }

      .btn-install:hover {
        opacity: 0.9;
      }

      .btn-install:disabled {
        opacity: 0.5;
        cursor: not-allowed;
        background: var(--t-muted);
      }

      .btn-installed {
        background: transparent;
        color: var(--t-muted);
        border: 1px solid var(--border-glass);
        padding: 6px 12px;
        border-radius: var(--radius-sm);
        font-weight: 500;
        cursor: default;
        font-size: var(--text-md);
      }

      .btn-remove {
        background: transparent;
        color: var(--t-muted);
        border: 1px solid var(--border-glass);
        padding: 6px 12px;
        border-radius: var(--radius-sm);
        cursor: pointer;
        font-weight: 500;
        transition: all 0.2s;
      }

      .btn-remove:hover {
        border-color: #e74c3c;
        color: #e74c3c;
      }

      .btn-remove:disabled {
        opacity: 0.5;
        cursor: not-allowed;
      }

      .btn-update {
        background: linear-gradient(135deg, hsl(35, 90%, 50%), hsl(25, 90%, 50%));
        color: white;
        border: none;
        padding: 6px 12px;
        border-radius: var(--radius-sm);
        cursor: pointer;
        font-weight: 500;
        transition: opacity 0.2s;
      }

      .btn-update:hover {
        opacity: 0.9;
      }

      .btn-update:disabled {
        opacity: 0.5;
        cursor: not-allowed;
      }

      .btn-fork {
        background: transparent;
        color: var(--t-muted);
        border: 1px solid var(--border-glass);
        padding: 6px 12px;
        border-radius: var(--radius-sm);
        cursor: pointer;
        font-weight: 500;
        transition: all 0.2s;
      }

      .btn-fork:hover {
        border-color: var(--color-primary);
        color: var(--color-primary);
      }

      .card.has-update {
        border-color: hsl(35, 80%, 50%);
      }

      .card.installed {
        border-color: var(--accent-secondary);
      }

      /* Actions Toolbar */
      .actions-toolbar {
        display: flex;
        gap: var(--space-sm);
        margin-bottom: var(--space-lg);
        padding: var(--space-md);
        background: var(--bg-glass);
        border: 1px solid var(--border-glass);
        border-radius: var(--radius-md);
        flex-wrap: wrap;
        align-items: center;
      }

      .toolbar-section {
        display: flex;
        gap: var(--space-sm);
        align-items: center;
      }

      .toolbar-section-title {
        font-size: var(--text-xs);
        text-transform: uppercase;
        letter-spacing: 0.05em;
        color: var(--t-muted);
        margin-right: var(--space-sm);
      }

      .toolbar-divider {
        width: 1px;
        height: 24px;
        background: var(--border-glass);
        margin: 0 var(--space-sm);
      }

      /* Unified button style for all toolbar buttons */
      .toolbar-btn {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        gap: 6px;
        height: 36px;
        padding: 0 14px;
        background: var(--bg-glass-strong);
        border: 1px solid var(--border-glass);
        color: var(--t-primary);
        border-radius: var(--radius-sm);
        cursor: pointer;
        font-size: var(--text-md);
        font-weight: 500;
        transition: all 0.2s;
        white-space: nowrap;
      }

      .toolbar-btn:hover {
        background: var(--accent-primary);
        border-color: var(--accent-primary);
        color: white;
      }

      .toolbar-btn:disabled {
        opacity: 0.5;
        cursor: not-allowed;
      }

      .toolbar-btn .icon {
        font-size: var(--text-lg);
        line-height: 1;
      }

      .toolbar-btn.primary {
        background: var(--accent-primary);
        border-color: var(--accent-primary);
        color: white;
      }

      .toolbar-btn.primary:hover {
        opacity: 0.9;
      }

      /* Legacy alias for backwards compatibility */
      .maker-btn {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        gap: 6px;
        height: 36px;
        padding: 0 14px;
        background: var(--bg-glass-strong);
        border: 1px solid var(--border-glass);
        color: var(--t-primary);
        border-radius: var(--radius-sm);
        cursor: pointer;
        font-size: var(--text-md);
        font-weight: 500;
        transition: all 0.2s;
        white-space: nowrap;
      }

      .maker-btn:hover {
        background: var(--accent-primary);
        border-color: var(--accent-primary);
        color: white;
      }

      .maker-btn:disabled {
        opacity: 0.5;
        cursor: not-allowed;
      }

      .maker-btn .icon {
        font-size: var(--text-lg);
        line-height: 1;
      }

      /* Add Marketplace Modal */
      .modal-overlay {
        position: fixed;
        inset: 0;
        background: rgba(0, 0, 0, 0.6);
        display: flex;
        align-items: center;
        justify-content: center;
        z-index: 1000;
        backdrop-filter: blur(4px);
      }

      .modal {
        background: var(--bg-panel);
        border: 1px solid var(--border-glass);
        border-radius: var(--radius-md);
        width: 90%;
        max-width: 450px;
        box-shadow: 0 20px 60px rgba(0, 0, 0, 0.4);
      }

      .modal-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: var(--space-md);
        border-bottom: 1px solid var(--border-glass);
      }

      .modal-header h3 {
        margin: 0;
        font-size: var(--text-lg);
      }

      .modal-close {
        background: none;
        border: none;
        color: var(--t-muted);
        cursor: pointer;
        padding: 4px;
        border-radius: var(--radius-sm);
      }

      .modal-close:hover {
        color: var(--t-primary);
        background: var(--bg-glass);
      }

      .modal-body {
        padding: var(--space-md);
      }

      .modal-body input {
        padding: var(--space-sm) var(--space-md);
        font-size: var(--text-md);
        margin-bottom: var(--space-sm);
      }

      .modal-hint {
        font-size: var(--text-sm);
        color: var(--t-muted);
        line-height: 1.5;
      }

      .modal-hint code {
        background: var(--bg-glass);
        padding: 1px 4px;
        border-radius: var(--radius-xs);
        font-size: var(--text-xs);
      }

      .formats-table {
        width: 100%;
        font-size: var(--text-sm);
        border-collapse: collapse;
        margin-top: var(--space-md);
      }

      .formats-table td {
        padding: 6px 0;
        vertical-align: middle;
      }

      .formats-table .type-label {
        color: var(--t-muted);
        font-size: var(--text-xs);
        width: 80px;
        font-weight: 500;
      }

      .formats-table td code {
        background: var(--bg-glass);
        padding: 2px 6px;
        border-radius: var(--radius-xs);
        font-size: var(--text-xs);
        color: var(--t-secondary);
      }

      .modal-footer {
        display: flex;
        justify-content: flex-end;
        gap: var(--space-sm);
        padding: var(--space-md);
        border-top: 1px solid var(--border-glass);
      }

      /* Detail modal */
      .detail-modal {
        max-width: 600px;
      }

      .detail-header {
        display: flex;
        align-items: center;
        gap: var(--space-sm);
        flex: 1;
        min-width: 0;
      }

      .detail-header .photon-icon {
        font-size: 1.4em;
      }

      .detail-header .card-title {
        margin-right: var(--space-sm);
      }

      .detail-meta {
        display: flex;
        align-items: center;
        gap: var(--space-sm);
        flex-wrap: wrap;
        padding: 0 var(--space-md);
        padding-bottom: var(--space-md);
        border-bottom: 1px solid var(--border-glass);
      }

      .detail-description {
        padding: var(--space-md);
        color: var(--t-secondary);
        line-height: 1.6;
        overflow-y: auto;
        max-height: 50vh;
      }

      .detail-description p {
        margin: 0 0 1em 0;
      }

      .detail-description p:last-child {
        margin-bottom: 0;
      }

      .detail-description code {
        background: var(--bg-glass);
        padding: 1px 4px;
        border-radius: var(--radius-xs);
        font-size: 0.9em;
      }

      .detail-description pre {
        background: var(--bg-glass);
        padding: var(--space-sm) var(--space-md);
        border-radius: var(--radius-sm);
        overflow-x: auto;
        margin: 0 0 1em 0;
      }

      .detail-description pre code {
        background: none;
        padding: 0;
      }

      .detail-description ul,
      .detail-description ol {
        padding-left: 1.5em;
        margin: 0 0 1em 0;
      }

      .detail-description h1,
      .detail-description h2,
      .detail-description h3,
      .detail-description h4 {
        margin: 0 0 0.5em 0;
        color: var(--t-primary);
      }

      .detail-description a {
        color: var(--accent-primary);
        text-decoration: none;
      }

      .detail-description a:hover {
        text-decoration: underline;
      }

      /* ===== Responsive Design ===== */
      @media (max-width: 768px) {
        .grid {
          grid-template-columns: 1fr;
          gap: var(--space-md);
        }

        .toolbar {
          flex-direction: column;
          gap: var(--space-sm);
        }

        .search-box input {
          min-height: 44px;
          font-size: 16px; /* Prevent iOS zoom */
        }

        .filter-pill {
          min-height: 44px;
        }

        .card {
          min-height: 44px;
        }

        .btn-install {
          min-height: 44px;
          width: 100%;
        }

        .actions-toolbar {
          flex-direction: column;
          align-items: stretch;
        }

        .toolbar-section {
          flex-wrap: wrap;
          justify-content: center;
        }

        .toolbar-divider {
          width: 100%;
          height: 1px;
          margin: var(--space-sm) 0;
        }

        .toolbar-btn,
        .maker-btn {
          min-height: 44px;
          flex: 1;
        }

        .modal {
          margin: var(--space-md);
          width: calc(100% - var(--space-lg));
        }

        .modal-footer {
          flex-direction: column;
        }

        .modal-footer button {
          width: 100%;
        }
      }

      /* Template picker modal */
      .template-modal {
        background: var(--bg-panel);
        border: 1px solid var(--border-glass);
        border-radius: 16px;
        padding: 28px;
        width: 90%;
        max-width: 520px;
        max-height: 80vh;
        overflow-y: auto;
        box-shadow: 0 20px 60px rgba(0, 0, 0, 0.4);
        position: relative;
      }

      .template-modal-subtitle {
        font-size: var(--text-xs);
        color: var(--t-muted);
        margin-bottom: 20px;
      }

      .template-list {
        display: flex;
        flex-direction: column;
        gap: 8px;
      }

      .template-card {
        display: flex;
        align-items: center;
        gap: 14px;
        background: var(--bg-glass);
        border: 1px solid var(--border-glass);
        border-radius: 10px;
        padding: 14px 16px;
        cursor: pointer;
        transition: all 0.15s ease;
      }

      .template-card:hover {
        border-color: var(--accent-primary);
        background: var(--bg-glass-strong);
        transform: translateX(2px);
      }

      .template-icon {
        font-size: 24px;
        width: 44px;
        height: 44px;
        display: flex;
        align-items: center;
        justify-content: center;
        border-radius: 10px;
        background: rgba(255, 255, 255, 0.05);
        flex-shrink: 0;
      }

      .template-info {
        flex: 1;
        min-width: 0;
      }

      .template-name {
        font-weight: 600;
        font-size: var(--text-sm);
        color: var(--t-primary);
        margin-bottom: 2px;
      }

      .template-desc {
        font-size: var(--text-xs);
        color: var(--t-muted);
        line-height: 1.4;
      }

      @media (max-width: 480px) {
        :host {
          padding: var(--space-sm);
        }

        .source-filters {
          flex-wrap: nowrap;
          overflow-x: auto;
          overflow-y: visible; /* Prevent focus ring clipping */
          padding: 2px var(--space-sm); /* Support focus ring on vertical edges */
          padding-bottom: var(--space-sm);
          -webkit-overflow-scrolling: touch;
        }

        .filter-pill {
          flex-shrink: 0;
        }

        .card-header {
          flex-direction: column;
          align-items: flex-start;
          gap: var(--space-sm);
        }

        .card-meta {
          width: 100%;
          justify-content: flex-start;
        }

        .card-title {
          font-size: 1rem;
        }

        .card-desc {
          font-size: 0.85rem;
        }

        .toolbar-section-title {
          width: 100%;
          text-align: center;
          margin-bottom: var(--space-xs);
        }

        .formats-table {
          font-size: 0.75rem;
        }
      }
    `,
  ];

  @state()
  private _items: MarketplaceItem[] = [];

  @state()
  private _allItems: MarketplaceItem[] = [];

  @state()
  private _sources: MarketplaceSource[] = [];

  @state()
  private _activeFilter: string | null = null;

  @state()
  private _activeTag: string | null = null;

  @state()
  private _loading = false;

  @state()
  private _installing: string | null = null;

  @state()
  private _removing: string | null = null;

  @state()
  private _updating: string | null = null;

  @state()
  private _showAddRepoModal = false;

  @state()
  private _showTemplates = false;

  @state()
  private _selectedItem: MarketplaceItem | null = null;

  @state()
  private _repoInput = '';

  connectedCallback() {
    super.connectedCallback();
    void this._fetchSources().then(() => this._fetchItems());
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    clearTimeout(this._searchTimeout);
  }

  render() {
    return html`
      <!-- Actions Toolbar -->
      <div class="actions-toolbar">
        <div class="toolbar-section">
          <span class="toolbar-section-title">Create</span>
          <button class="toolbar-btn" @click=${() => this._createNew()} title="Create a new photon">
            <span class="icon">${sparkle}</span>
            <span>New Photon</span>
          </button>
        </div>

        <div class="toolbar-divider"></div>

        <div class="toolbar-section">
          <span class="toolbar-section-title">Marketplaces</span>
          <button
            class="toolbar-btn primary"
            @click=${() => (this._showAddRepoModal = true)}
            title="Add a new marketplace"
          >
            <span class="icon">+</span>
            <span>Add Marketplace</span>
          </button>
          <button
            class="toolbar-btn"
            @click=${() => {
              void this._syncPhotons();
            }}
            title="Sync marketplace cache"
          >
            <span class="icon">${refreshIcon}</span>
            <span>Sync</span>
          </button>
        </div>

        <div class="toolbar-divider"></div>

        <div class="toolbar-section">
          <span class="toolbar-section-title">Tools</span>
          <button
            class="toolbar-btn"
            @click=${() => this._validatePhotons()}
            title="Validate all photons"
          >
            <span class="icon">${check}</span>
            <span>Validate</span>
          </button>
        </div>
      </div>

      <div class="toolbar">
        <div class="search-box">
          <input
            type="text"
            placeholder="Search photons..."
            @input=${(e: Event) => this._handleSearch(e)}
          />
        </div>
      </div>

      <!-- Marketplace filters -->
      <div class="source-filters">
        <button
          class="filter-pill ${this._activeFilter === null ? 'active' : ''}"
          @click=${() => this._filterBySource(null)}
        >
          All
          <span class="count">${this._allItems.length}</span>
        </button>
        ${this._sources
          .filter((s) => s.enabled && !s.name.endsWith('.photon.ts'))
          .map(
            (source) => html`
              <button
                class="filter-pill ${this._activeFilter === source.name ? 'active' : ''}"
                @click=${() => this._filterBySource(source.name)}
                title=${source.source}
              >
                ${source.name}
                <span class="count">${source.photonCount}</span>
                ${source.builtIn
                  ? ''
                  : html`<span
                      class="remove-btn"
                      @click=${(e: Event) => {
                        e.stopPropagation();
                        void this._removeSource(source.name);
                      }}
                      title="Remove marketplace"
                      aria-label="Remove marketplace"
                      >${xMark}</span
                    >`}
              </button>
            `
          )}
      </div>

      ${this._allTags.length > 0
        ? html`
            <div class="source-filters" style="padding-top: 0;">
              ${this._allTags.map(
                (tag) => html`
                  <button
                    class="filter-pill ${this._activeTag === tag ? 'active' : ''}"
                    @click=${() => this._filterByTag(this._activeTag === tag ? null : tag)}
                    style="font-size: var(--text-xs);"
                  >
                    ${tag}
                  </button>
                `
              )}
            </div>
          `
        : ''}
      ${this._loading
        ? html`<div style="text-align: center; color: var(--t-muted); padding: 40px;">
            Loading marketplace...
          </div>`
        : html` <div class="grid">${this._items.map((item) => this._renderItem(item))}</div> `}
      ${this._showAddRepoModal ? this._renderAddRepoModal() : ''}
      ${this._showTemplates ? this._renderTemplateModal() : ''}
      ${this._selectedItem ? this._renderDetailModal() : ''}
    `;
  }

  private _renderTemplateModal() {
    return html`
      <div
        class="modal-overlay"
        @click=${(e: Event) => {
          if (e.target === e.currentTarget) this._showTemplates = false;
        }}
      >
        <div
          class="template-modal"
          role="dialog"
          aria-modal="true"
          aria-labelledby="template-modal-title"
        >
          <div class="modal-header" style="padding: 0; border: none; margin-bottom: 4px;">
            <h3
              id="template-modal-title"
              style="margin: 0; font-size: var(--text-lg); font-weight: 600; color: var(--t-primary);"
            >
              Create New Photon
            </h3>
            <button
              class="modal-close"
              @click=${() => (this._showTemplates = false)}
              aria-label="Close"
            >
              ${xMark}
            </button>
          </div>
          <div class="template-modal-subtitle">
            Start from a template or begin with a blank file
          </div>

          <div class="template-list">
            <div
              class="template-card"
              role="button"
              tabindex="0"
              @click=${() => this._createFromTemplate()}
              @keydown=${(e: KeyboardEvent) =>
                (e.key === 'Enter' || e.key === ' ') &&
                (e.preventDefault(), this._createFromTemplate())}
            >
              <div class="template-icon">${fileIcon}</div>
              <div class="template-info">
                <div class="template-name">Blank</div>
                <div class="template-desc">Empty photon — start from scratch</div>
              </div>
            </div>
            ${templates.map(
              (t) => html`
                <div class="template-card" @click=${() => this._createFromTemplate(t.source)}>
                  <div class="template-icon">${t.icon}</div>
                  <div class="template-info">
                    <div class="template-name">${t.name}</div>
                    <div class="template-desc">${t.description}</div>
                  </div>
                </div>
              `
            )}
          </div>
        </div>
      </div>
    `;
  }

  private _renderDetailModal() {
    const item = this._selectedItem!;
    const sourceClass = this._getSourceClass(item.marketplace);
    const isInstalling = this._installing === item.name;
    const isUpdating = this._updating === item.name;
    const isRemoving = this._removing === item.name;
    const descriptionHtml = item.description
      ? marked.parse(item.description)
      : '<p style="color: var(--t-muted); font-style: italic;">No description available.</p>';

    return html`
      <div
        class="modal-overlay"
        @click=${(e: Event) => {
          if (e.target === e.currentTarget) this._selectedItem = null;
        }}
      >
        <div
          class="modal detail-modal"
          role="dialog"
          aria-modal="true"
          aria-labelledby="detail-modal-title"
        >
          <div class="modal-header">
            <div class="detail-header">
              ${item.icon ? html`<span class="photon-icon">${item.icon}</span>` : ''}
              <span class="card-title" id="detail-modal-title"
                >${item.displayName || item.name}</span
              >
              <div
                class="tag"
                style="background: var(--accent-secondary); color: white; font-weight: 600;"
              >
                ${item.version}
              </div>
              <span class="source-pill ${sourceClass}">${item.marketplace || 'Local'}</span>
            </div>
            <button
              class="modal-close"
              @click=${() => (this._selectedItem = null)}
              aria-label="Close"
            >
              ${xMark}
            </button>
          </div>
          <div class="detail-meta">
            ${item.author && item.author !== 'Unknown'
              ? html`<span style="font-size: var(--text-sm); color: var(--t-muted);"
                  >by ${item.author}</span
                >`
              : ''}
            ${item.tags.length
              ? html`<div class="tags" style="margin: 0;">
                  ${item.tags.map((tag) => html`<span class="tag">${tag}</span>`)}
                </div>`
              : ''}
          </div>
          <div class="detail-description">${unsafeHTML(descriptionHtml)}</div>
          <div class="modal-footer">
            ${item.installed && item.hasUpdate
              ? html`<button
                    class="btn-update"
                    ?disabled=${isUpdating}
                    @click=${() => this._update(item)}
                  >
                    ${isUpdating
                      ? 'Updating...'
                      : `Update${item.latestVersion ? ` to ${item.latestVersion}` : ''}`}
                  </button>
                  <button
                    class="btn-remove"
                    ?disabled=${isRemoving}
                    @click=${() => this._remove(item)}
                  >
                    ${isRemoving ? 'Removing...' : 'Remove'}
                  </button>`
              : item.installed
                ? html`<button
                    class="btn-remove"
                    ?disabled=${isRemoving}
                    @click=${() => this._remove(item)}
                  >
                    ${isRemoving ? 'Removing...' : 'Remove'}
                  </button>`
                : html`<button
                    class="btn-install"
                    ?disabled=${isInstalling}
                    @click=${() => this._install(item)}
                  >
                    ${isInstalling ? 'Installing...' : 'Install'}
                  </button>`}
          </div>
        </div>
      </div>
    `;
  }

  private _renderAddRepoModal() {
    return html`
      <div
        class="modal-overlay"
        @click=${(e: Event) => {
          if (e.target === e.currentTarget) this._showAddRepoModal = false;
        }}
      >
        <div class="modal" role="dialog" aria-modal="true" aria-labelledby="add-repo-modal-title">
          <div class="modal-header">
            <h3 id="add-repo-modal-title">Add Marketplace</h3>
            <button
              class="modal-close"
              @click=${() => (this._showAddRepoModal = false)}
              aria-label="Close"
            >
              ${xMark}
            </button>
          </div>
          <div class="modal-body">
            <input
              type="text"
              placeholder="Enter GitHub repo, local path, or URL"
              .value=${this._repoInput}
              @input=${(e: Event) => (this._repoInput = (e.target as HTMLInputElement).value)}
              @keydown=${(e: KeyboardEvent) => {
                if (e.key === 'Enter') void this._addRepository();
              }}
              autofocus
            />
            <table class="formats-table">
              <tbody>
                <tr>
                  <td class="type-label">GitHub</td>
                  <td><code>username/repo</code></td>
                </tr>
                <tr>
                  <td class="type-label">GitHub URL</td>
                  <td><code>https://github.com/user/repo</code></td>
                </tr>
                <tr>
                  <td class="type-label">SSH</td>
                  <td><code>git@github.com:user/repo.git</code></td>
                </tr>
                <tr>
                  <td class="type-label">Local</td>
                  <td><code>~/path/to/folder</code></td>
                </tr>
                <tr>
                  <td class="type-label">URL</td>
                  <td><code>https://example.com/photons.json</code></td>
                </tr>
              </tbody>
            </table>
          </div>
          <div class="modal-footer">
            <button class="toolbar-btn" @click=${() => (this._showAddRepoModal = false)}>
              Cancel
            </button>
            <button
              class="toolbar-btn primary"
              @click=${() => {
                void this._addRepository();
              }}
            >
              Add
            </button>
          </div>
        </div>
      </div>
    `;
  }

  private _renderItem(item: MarketplaceItem) {
    const isInstalling = this._installing === item.name;
    const isUpdating = this._updating === item.name;
    const sourceClass = this._getSourceClass(item.marketplace);
    const cardClasses = `card glass ${item.internal ? 'internal' : ''} ${item.installed ? 'installed' : ''} ${item.hasUpdate ? 'has-update' : ''}`;

    // Icon: emoji if present, otherwise colored initials
    const hasIcon = !!item.icon;
    let iconHueStyle = '';
    if (!hasIcon) {
      let hash = 0;
      for (let i = 0; i < item.name.length; i++) {
        hash = item.name.charCodeAt(i) + ((hash << 5) - hash);
      }
      const hue = Math.abs(hash) % 360;
      iconHueStyle = `--icon-hue: ${hue}`;
    }
    const displayIcon = hasIcon ? item.icon : item.name.substring(0, 2).toUpperCase();

    return html`
      <div
        class="${cardClasses}"
        role="button"
        tabindex="0"
        aria-label="${item.name}: ${item.description}"
        @click=${() => (this._selectedItem = item)}
        @keydown=${(e: KeyboardEvent) =>
          (e.key === 'Enter' || e.key === ' ') && (e.preventDefault(), (this._selectedItem = item))}
      >
        <div class="card-body">
          <div
            class="card-icon-large ${hasIcon ? 'emoji-icon' : 'initials-icon'}"
            style="${iconHueStyle}"
            aria-hidden="true"
          >
            ${displayIcon}
          </div>
          <div class="card-content">
            <div class="card-title-row">
              <span class="card-title">${item.displayName || item.name}</span>
              <div class="card-meta">
                ${item.internal
                  ? html`<span class="source-pill source-internal">System</span>`
                  : ''}
                <span class="source-pill ${sourceClass}">${item.marketplace || 'Local'}</span>
                <span
                  class="tag"
                  style="background: var(--accent-secondary); color: white; font-weight: 600;"
                  >${item.version}</span
                >
              </div>
            </div>
            ${item.author && item.author !== 'Unknown'
              ? html`<div class="card-author">by ${item.author}</div>`
              : ''}
            <div class="card-desc" title="${item.description}">${item.description}</div>
            ${item.tags.length > 0
              ? html`<div class="tags">
                  ${item.tags.map((tag) => html`<span class="tag">${tag}</span>`)}
                </div>`
              : ''}
          </div>
        </div>

        <div class="card-footer" @click=${(e: Event) => e.stopPropagation()}>
          ${item.installed && item.hasUpdate
            ? html`<button
                  class="btn-update"
                  ?disabled=${isUpdating}
                  @click=${() => this._update(item)}
                >
                  ${isUpdating
                    ? 'Updating...'
                    : `Update${item.latestVersion ? ` to ${item.latestVersion}` : ''}`}
                </button>
                <button class="btn-fork btn-secondary-push" @click=${() => this._fork(item)}>
                  Fork
                </button>
                <button
                  class="btn-remove"
                  ?disabled=${this._removing === item.name}
                  @click=${() => this._remove(item)}
                >
                  ${this._removing === item.name ? 'Removing...' : 'Remove'}
                </button>`
            : item.installed
              ? html`<button class="btn-fork btn-secondary-push" @click=${() => this._fork(item)}>
                    Fork
                  </button>
                  <button
                    class="btn-remove"
                    ?disabled=${this._removing === item.name}
                    @click=${() => this._remove(item)}
                  >
                    ${this._removing === item.name ? 'Removing...' : 'Remove'}
                  </button>`
              : html`<button
                  class="btn-install"
                  ?disabled=${isInstalling}
                  @click=${() => this._install(item)}
                >
                  ${isInstalling ? 'Installing...' : 'Install'}
                </button>`}
        </div>
      </div>
    `;
  }

  private _getSourceClass(marketplace: string): string {
    const lower = marketplace?.toLowerCase() || '';
    if (lower.includes('official') || lower === 'photon' || lower === 'portel') {
      return 'source-official';
    }
    if (lower.includes('community') || lower.includes('github')) {
      return 'source-community';
    }
    return 'source-local';
  }

  private _getSourceLabel(marketplace: string): string {
    const lower = marketplace?.toLowerCase() || '';
    if (lower.includes('official') || lower === 'photon' || lower === 'portel') {
      return 'Official';
    }
    if (lower.includes('community')) {
      return 'Community';
    }
    if (lower.includes('github')) {
      return 'GitHub';
    }
    return marketplace || 'Local';
  }

  private async _fetchSources() {
    try {
      const res = await fetch('/api/marketplace/sources', {
        signal: AbortSignal.timeout(10000),
      });
      const data = await res.json();
      this._sources = data.sources || [];
    } catch (e) {
      console.error('Failed to fetch marketplace sources', e);
    }
  }

  private async _fetchItems(query = '') {
    this._loading = true;
    try {
      const endpoint = query
        ? `/api/marketplace/search?q=${encodeURIComponent(query)}`
        : '/api/marketplace/list';

      const res = await fetch(endpoint, {
        signal: AbortSignal.timeout(30000), // marketplace fetch can be slow
      });
      const data = await res.json();
      this._allItems = data.photons || [];
      this._disambiguateNames();
      this._applyFilter();
    } catch (e) {
      console.error('Failed to fetch marketplace', e);
    } finally {
      this._loading = false;
    }
  }

  private _filterBySource(sourceName: string | null) {
    this._activeFilter = sourceName;
    this._applyFilter();
  }

  private _filterByTag(tag: string | null) {
    this._activeTag = tag;
    this._applyFilter();
  }

  private get _allTags(): string[] {
    const tags = new Set<string>();
    for (const item of this._allItems) {
      for (const tag of item.tags) tags.add(tag);
    }
    return [...tags].sort();
  }

  private _disambiguateNames() {
    const counts = new Map<string, number>();
    for (const item of this._allItems) {
      counts.set(item.name, (counts.get(item.name) || 0) + 1);
    }
    for (const item of this._allItems) {
      item.displayName =
        (counts.get(item.name) || 0) > 1 ? `${item.name} (${item.marketplace})` : item.name;
    }
  }

  private _applyFilter() {
    let items = this._allItems;
    if (this._activeFilter !== null) {
      items = items.filter((item) => item.marketplace === this._activeFilter);
    }
    if (this._activeTag !== null) {
      items = items.filter((item) => item.tags.includes(this._activeTag!));
    }
    this._items = items;
  }

  private async _removeSource(name: string) {
    if (
      !(await confirmElicit(`Remove marketplace "${name}"? This will not uninstall any photons.`, {
        confirm: 'Remove',
        destructive: true,
      }))
    ) {
      return;
    }

    try {
      const res = await fetch('/api/marketplace/sources/remove', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Photon-Request': '1' },
        body: JSON.stringify({ name }),
        signal: AbortSignal.timeout(10000),
      });

      if (res.ok) {
        showToast(`Removed marketplace: ${name}`, 'success');
        if (this._activeFilter === name) {
          this._activeFilter = null;
        }
        await this._fetchSources();
        await this._fetchItems();
      } else {
        const data = await res.json();
        throw new Error(data.error || 'Failed to remove marketplace');
      }
    } catch (e: any) {
      showToast(e.message, 'error');
    }
  }

  // Debounce search
  private _searchTimeout: any;
  private _handleSearch(e: Event) {
    const query = (e.target as HTMLInputElement).value;
    clearTimeout(this._searchTimeout);
    this._searchTimeout = setTimeout(() => {
      void this._fetchItems(query);
    }, 300);
  }

  private async _install(item: MarketplaceItem) {
    this._installing = item.name;
    try {
      const res = await fetch('/api/marketplace/add', {
        method: 'POST',
        headers: { 'X-Photon-Request': '1' },
        body: JSON.stringify({ name: item.name }),
        signal: AbortSignal.timeout(30000), // 30s for installation
      });

      if (res.ok) {
        // Mark item as installed in the local list
        item.installed = true;
        this.requestUpdate();
        this.dispatchEvent(
          new CustomEvent('install', {
            detail: { name: item.name },
            bubbles: true,
            composed: true,
          })
        );
        showToast(`Successfully installed ${item.name}`, 'success');
      } else {
        const err = await res.json();
        showToast(`Failed to install: ${err.error}`, 'error', 5000);
      }
    } catch (e) {
      showToast('Installation failed', 'error', 5000);
    } finally {
      this._installing = null;
    }
  }

  private async _remove(item: MarketplaceItem) {
    // Confirmation dialog for destructive action
    if (
      !(await confirmElicit(`Remove "${item.name}"? This will delete the photon files.`, {
        confirm: 'Remove',
        destructive: true,
      }))
    )
      return;
    this._removing = item.name;
    try {
      const res = await fetch('/api/marketplace/remove', {
        method: 'POST',
        headers: { 'X-Photon-Request': '1' },
        body: JSON.stringify({ name: item.name }),
        signal: AbortSignal.timeout(10000),
      });

      if (res.ok) {
        item.installed = false;
        this.requestUpdate();
        this.dispatchEvent(
          new CustomEvent('install', {
            detail: { name: item.name, removed: true },
            bubbles: true,
            composed: true,
          })
        );
        showToast(`Removed ${item.name}`, 'success');
      } else {
        const err = await res.json();
        showToast(`Failed to remove: ${err.error}`, 'error', 5000);
      }
    } catch (e) {
      showToast('Remove failed', 'error', 5000);
    } finally {
      this._removing = null;
    }
  }

  private async _update(item: MarketplaceItem) {
    this._updating = item.name;
    try {
      // Re-install overwrites the existing file and updates metadata
      const res = await fetch('/api/marketplace/add', {
        method: 'POST',
        headers: { 'X-Photon-Request': '1' },
        body: JSON.stringify({ name: item.name }),
        signal: AbortSignal.timeout(30000),
      });

      if (res.ok) {
        item.hasUpdate = false;
        item.latestVersion = undefined;
        this.requestUpdate();
        this.dispatchEvent(
          new CustomEvent('install', {
            detail: { name: item.name, updated: true },
            bubbles: true,
            composed: true,
          })
        );
        showToast(`Successfully updated ${item.name}`, 'success');
      } else {
        const err = await res.json();
        showToast(`Failed to update: ${err.error}`, 'error', 5000);
      }
    } catch (e) {
      showToast('Update failed', 'error', 5000);
    } finally {
      this._updating = null;
    }
  }

  private _fork(item: MarketplaceItem) {
    this.dispatchEvent(
      new CustomEvent('fork-photon', {
        detail: { name: item.name },
        bubbles: true,
        composed: true,
      })
    );
  }

  // Maker static method actions
  private _createNew() {
    this._showTemplates = true;
  }

  private _createFromTemplate(templateSource?: string) {
    this._showTemplates = false;
    this.dispatchEvent(
      new CustomEvent('maker-action', {
        detail: { action: 'new', templateSource },
        bubbles: true,
        composed: true,
      })
    );
  }

  private async _syncPhotons() {
    this.dispatchEvent(
      new CustomEvent('maker-action', {
        detail: { action: 'sync' },
        bubbles: true,
        composed: true,
      })
    );
    // Re-fetch items and source counts after sync to pick up fresh manifests
    setTimeout(() => {
      void this._fetchSources();
      void this._fetchItems();
    }, 2000);
  }

  private _validatePhotons() {
    this.dispatchEvent(
      new CustomEvent('maker-action', {
        detail: { action: 'validate' },
        bubbles: true,
        composed: true,
      })
    );
  }

  private async _addRepository() {
    const source = this._repoInput.trim();
    if (!source) {
      showToast('Please enter a marketplace location', 'error');
      return;
    }

    try {
      const res = await fetch('/api/marketplace/sources/add', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Photon-Request': '1' },
        body: JSON.stringify({ source }),
        signal: AbortSignal.timeout(30000), // 30s for adding repo
      });

      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.error || 'Failed to add repository');
      }

      this._showAddRepoModal = false;
      this._repoInput = '';
      showToast(
        data.added ? `Added marketplace: ${data.name}` : `Already exists: ${data.name}`,
        'success'
      );

      // Refresh sources and photons
      await this._fetchSources();
      await this._fetchItems();
    } catch (e: any) {
      showToast(e.message || 'Failed to add repository', 'error', 5000);
    }
  }
}
